// API utilities for popup service
import type {
    PopupSettings,
    PopupSubmissionData,
    PopupSettingsResponse,
    PopupSubmissionResponse,
    CreatePopupSettingsRequest,
    CreatePopupSettingsResponse,
    PopupWidgetConfig,
    PopupWheelSpinResult,
} from "@/shared/types/popup-types";
import { SERVICE_BASE } from "@/lib/embed-script";

// ============================================
// API CONFIGURATION
// ============================================

export function getApiBaseUrl(config?: PopupWidgetConfig): string {
    if (config?.apiBaseUrl) {
        return config.apiBaseUrl;
    }

    // The host the storefront script was loaded from (popups.flowapt.com or
    // the vercel.app name), else the vercel.app name. See embed-script.ts.
    return SERVICE_BASE;

    // // Check environment - development vs production
    // const isDevelopment = process.env.NODE_ENV === 'development' ||
    //     (typeof window !== 'undefined' &&
    //         (window.location.hostname === 'localhost' ||
    //             window.location.hostname === '127.0.0.1' ||
    //             window.location.hostname.includes('localhost')));

    // if (isDevelopment) {
    //     return 'http://localhost:3000';
    // } else {
    //     return 'https://flowapt-popup-service.vercel.app';
    // }
}

// ============================================
// ERROR HANDLING
// ============================================

export class PopupApiError extends Error {
    constructor(
        message: string,
        public status?: number,
        public details?: string
    ) {
        super(message);
        this.name = 'PopupApiError';
    }
}

function handleApiResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
        return response.json().then(
            (errorData) => {
                throw new PopupApiError(
                    errorData.error || 'API request failed',
                    response.status,
                    errorData.details
                );
            },
            () => {
                throw new PopupApiError(
                    `HTTP ${response.status}: ${response.statusText}`,
                    response.status
                );
            }
        );
    }

    return response.json();
}

// ============================================
// VISITOR INFO COLLECTION
// ============================================

function getVisitorMetadata() {
    if (typeof window === 'undefined') {
        return {};
    }

    return {
        user_agent: navigator.userAgent,
        referrer_url: document.referrer || undefined,
        // Note: IP address will be captured server-side
    };
}

// ============================================
// API FUNCTIONS
// ============================================

const VISITOR_ID_KEY = 'flowapt_popup_vid';

/** A random id kept in localStorage, used only to keep an A/B test's
 *  variant the same for one visitor. Null when storage is unavailable. */
export function getVisitorId(): string | null {
    if (typeof window === 'undefined') return null;
    try {
        const existing = localStorage.getItem(VISITOR_ID_KEY);
        if (existing && /^[A-Za-z0-9_-]{8,64}$/.test(existing)) return existing;
        const fresh = typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID()
            : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
        localStorage.setItem(VISITOR_ID_KEY, fresh);
        return fresh;
    } catch {
        return null;
    }
}

export async function fetchPopupSettings(
    organizationId: string,
    popupId?: string,
    shopifyCustomerId?: string,
    activeOnStore?: boolean,
    config?: PopupWidgetConfig,
    /** Hosted landing page (/p/…) — the server skips country targeting so a
     *  shared link always renders. */
    pageMode?: boolean
): Promise<{ data: PopupSettings | null; skipPopup: boolean }> {
    const baseUrl = getApiBaseUrl(config);
    console.log(`baseUrl: ${baseUrl}`);

    // Resolution: when activeOnStore is set the server serves the org's active-on-store popup
    // (falling back to popupId if none is live); otherwise it serves the specific popupId.
    let url = `${baseUrl}/api/popup-settings?organizationId=${encodeURIComponent(organizationId)}`;
    if (popupId) {
        url += `&popupId=${encodeURIComponent(popupId)}`;
    }
    if (activeOnStore) {
        url += `&activeOnStore=true`;
    }
    if (shopifyCustomerId) {
        url += `&shopifyCustomerId=${encodeURIComponent(shopifyCustomerId)}`;
    }
    if (pageMode) {
        url += `&pageMode=true`;
    }
    // A/B tests: a stable id per browser so a visitor always gets the same
    // variant (the server hashes it; nothing else is stored against it).
    const visitorId = getVisitorId();
    if (visitorId) {
        url += `&visitorId=${encodeURIComponent(visitorId)}`;
    }

    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        const data: PopupSettingsResponse & { skipPopup?: boolean } = await handleApiResponse(response);

        if (data.skipPopup) {
            return { data: null, skipPopup: true };
        }

        if (!data.success || !data.data) {
            throw new PopupApiError(data.error || 'Failed to fetch popup settings');
        }

        return { data: data.data, skipPopup: false };
    } catch (error) {
        if (error instanceof PopupApiError) {
            throw error;
        }

        if (config?.debug) {
            console.error('Failed to fetch popup settings:', error);
        }

        throw new PopupApiError('Network error while fetching popup settings');
    }
}

export async function submitPopupData(
    organizationId: string,
    popupId: string,
    submissionData: PopupSubmissionData,
    config?: PopupWidgetConfig
): Promise<{ id: string; message: string; wheel?: PopupWheelSpinResult }> {
    const baseUrl = getApiBaseUrl(config);
    const url = `${baseUrl}/api/popup-submissions`;

    const payload = {
        organizationId,
        popupId,
        submissionData,
        ...getVisitorMetadata(),
    };
    console.log(`payload `, payload)

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });

        const data: PopupSubmissionResponse = await handleApiResponse(response);

        if (!data.success || !data.data) {
            throw new PopupApiError(data.error || 'Failed to submit popup data');
        }

        return data.data;
    } catch (error) {
        if (error instanceof PopupApiError) {
            throw error;
        }

        if (config?.debug) {
            console.error('Failed to submit popup data:', error);
        }

        throw new PopupApiError('Network error while submitting popup data');
    }
}

/**
 * Read back the discount code minted for ONE signup (21 Aug 2026).
 *
 * The code is created by the discount webhook a moment AFTER the submission is
 * saved, so the caller polls this until it appears. Returns null for "not yet",
 * "this popup issues no code", and every failure alike — a missing code must
 * degrade to the success screen's message copy, never to a visible error.
 */
/**
 * Play-first games (form_placement "after"): ask the server to decide the
 * prize BEFORE any lead exists. It comes back signed; the token rides onto
 * the later submission so the recorded prize is the one the visitor saw.
 */
export async function requestWheelSpin(
    organizationId: string,
    popupId: string,
    config?: PopupWidgetConfig
): Promise<{ wheel: PopupWheelSpinResult; token: string }> {
    const baseUrl = getApiBaseUrl(config);
    const url = `${baseUrl}/api/popup-spin`;
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ organizationId, popupId }),
        });
        const data = await handleApiResponse(response) as { success: boolean; data?: { wheel: PopupWheelSpinResult; token: string }; error?: string };
        if (!data.success || !data.data) {
            throw new PopupApiError(data.error || 'Could not start the game');
        }
        return data.data;
    } catch (error) {
        if (error instanceof PopupApiError) throw error;
        throw new PopupApiError('Network error while starting the game');
    }
}

export async function fetchIssuedDiscountCode(
    organizationId: string,
    submissionId: string,
    config?: PopupWidgetConfig
): Promise<string | null> {
    const baseUrl = getApiBaseUrl(config);
    const url =
        `${baseUrl}/api/popup-discount-code` +
        `?organizationId=${encodeURIComponent(organizationId)}` +
        `&submissionId=${encodeURIComponent(submissionId)}`;

    try {
        const response = await fetch(url, { method: 'GET' });
        if (!response.ok) return null;

        const data = await response.json();
        return typeof data?.code === 'string' && data.code.trim() ? data.code.trim() : null;
    } catch (error) {
        if (config?.debug) {
            console.error('Failed to read issued discount code:', error);
        }
        return null;
    }
}

/** What the signup's pass actually is, straight from the server. */
export interface IssuedPass {
    serial: string;
    apple_url: string;
    install_url: string;
    has_google: boolean;
    /** True when the signup resolved to a card the customer already held. */
    is_existing: boolean;
    /** The card's REAL standing, so the preview stops showing a returning
     *  member 0 points and Bronze. Null when the org runs no points engine. */
    points: number | null;
    tier: string | null;
    orders: number | null;
    member_name: string | null;
}

/**
 * The pass issued for one signup. Like the discount code above, it is minted a
 * moment after the submission lands, so the caller polls until it appears.
 *
 * This REPLACED deriving the serial in the browser. The old client-side
 * derivation had to mirror the edge function's hashing exactly, and it silently
 * stopped matching the day the server began reusing a returning member's card —
 * handing people a link to a pass that was never theirs. The server is now the
 * only thing that decides which card a signup gets.
 *
 * Null means "not yet", "this popup issues no pass", and every failure alike.
 */
export async function fetchIssuedPass(
    organizationId: string,
    submissionId: string,
    config?: PopupWidgetConfig
): Promise<IssuedPass | null> {
    const baseUrl = getApiBaseUrl(config);
    const url =
        `${baseUrl}/api/popup-pass` +
        `?organizationId=${encodeURIComponent(organizationId)}` +
        `&submissionId=${encodeURIComponent(submissionId)}`;

    try {
        const response = await fetch(url, { method: 'GET' });
        if (!response.ok) return null;

        const data = await response.json();
        const pass = data?.pass;
        if (!pass || typeof pass.serial !== 'string' || !pass.serial.trim()) return null;

        return {
            serial: pass.serial,
            apple_url: String(pass.apple_url || ''),
            install_url: String(pass.install_url || ''),
            has_google: pass.has_google === true,
            is_existing: pass.is_existing === true,
            points: typeof pass.points === 'number' ? pass.points : null,
            tier: typeof pass.tier === 'string' && pass.tier ? pass.tier : null,
            orders: typeof pass.orders === 'number' ? pass.orders : null,
            member_name:
                typeof pass.member_name === 'string' && pass.member_name ? pass.member_name : null
        };
    } catch (error) {
        if (config?.debug) {
            console.error('Failed to read issued pass:', error);
        }
        return null;
    }
}

export async function createPopupSettings(
    request: CreatePopupSettingsRequest,
    config?: PopupWidgetConfig
): Promise<{ id: string; message: string; popup: PopupSettings }> {
    const baseUrl = getApiBaseUrl(config);
    const url = `${baseUrl}/api/popup-settings`;

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
        });

        const data: CreatePopupSettingsResponse = await handleApiResponse(response);

        if (!data.success || !data.data) {
            throw new PopupApiError(data.error || 'Failed to create popup settings');
        }

        return data.data;
    } catch (error) {
        if (error instanceof PopupApiError) {
            throw error;
        }

        if (config?.debug) {
            console.error('Failed to create popup settings:', error);
        }

        throw new PopupApiError('Network error while creating popup settings');
    }
}

// ============================================
// IMPRESSION TRACKING (display beacon)
// ============================================

/** Counts ONE real impression — called when the popup actually becomes
 *  visible to a visitor (10 Aug 2026). Before this, total_impressions was
 *  incremented by the settings fetch itself, i.e. it counted PAGE LOADS:
 *  frequency-capped, device-blocked and never-triggered visitors all
 *  inflated the denominator of every conversion rate. Fire-and-forget —
 *  an analytics failure must never affect the popup. */
export async function reportPopupDisplay(
    popupId: string,
    config?: PopupWidgetConfig
): Promise<void> {
    const baseUrl = getApiBaseUrl(config);
    try {
        await fetch(`${baseUrl}/api/popup-impression`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ popupId }),
        });
    } catch (error) {
        if (config?.debug) {
            console.warn('Failed to report popup display:', error);
        }
    }
}

// ============================================
// RETRY LOGIC
// ============================================

export async function withRetry<T>(
    fn: () => Promise<T>,
    maxRetries: number = 3,
    delay: number = 1000
): Promise<T> {
    let lastError: Error;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            return await fn();
        } catch (error) {
            lastError = error as Error;

            if (attempt === maxRetries) {
                break;
            }

            // Wait before retrying
            await new Promise(resolve => setTimeout(resolve, delay * attempt));
        }
    }

    throw lastError!;
}

// (The old batch-operations queue was deleted 10 Aug 2026: it POSTed to
// /api/popup-settings/impression — an endpoint that never existed — and its
// entry point was never called from anywhere.)
